// Static design tokens + lookup tables for the AI Reasoning Path module.
// Pure data only (no JSX, no logic). Mirrors deep-signal/constants.ts.

import type { StageId, Priority, OptimizationType } from './types';

// ── Documented hardcoded-hex exceptions (spec §0.3) ──────────────────────────
// Tokens don't cover teal/violet used AS TEXT, nor on-bright-teal foreground.
export const TEAL_TEXT = '#04938F';      // teal-as-text (light); dark flips to #2DD4CE
export const ON_TEAL_TEXT = '#04312F';   // foreground on a bright-teal fill (never white)
export const TEAL_BAR_END = '#2DD4CE';   // confidence-meter gradient end-stop

// Pipeline connector lerp stops: teal #00C9C4 -> violet #7E5FE0 at 0/.33/.67/1.
export const CONNECTOR_GRADIENTS = [
  'linear-gradient(#00C9C4,#2AA6CD)', // node 1 -> 2
  'linear-gradient(#2AA6CD,#5482D7)', // node 2 -> 3
  'linear-gradient(#5482D7,#7E5FE0)', // node 3 -> 4
];

// ── Stage ordering + accents (spec §7.1) ─────────────────────────────────────
export const STAGE_ORDER: StageId[] = [
  'task-setup',
  'evidence-signals',
  'understanding-decisions',
  'final-synthesis',
];

export const STAGE_ACCENTS: Record<StageId, string> = {
  'task-setup': 'var(--signal-blue)',
  'evidence-signals': 'var(--infrared-coral)',
  'understanding-decisions': 'var(--spectral-violet)',
  'final-synthesis': 'var(--alpha-teal)',
};

export const STAGE_LOADING_COPY: Record<StageId, string> = {
  'task-setup': 'Framing the task…',
  'evidence-signals': 'Scanning sources…',
  'understanding-decisions': 'Comparing rankings…',
  'final-synthesis': 'Synthesizing…',
};

// "Stage N" chip label by id.
export const STAGE_NUMBER: Record<StageId, string> = {
  'task-setup': '1',
  'evidence-signals': '2',
  'understanding-decisions': '3',
  'final-synthesis': '4',
};

// ── Priority badge styles (spec §6 / §7.2) ───────────────────────────────────
export interface BadgeStyle {
  bg: string;
  border: string;
  color: string;
}

export const PRIORITY_STYLES: Record<Priority, BadgeStyle> = {
  High: { bg: 'var(--infrared-coral-10)', border: 'var(--infrared-coral-20)', color: 'var(--infrared-coral)' },
  Medium: { bg: 'var(--warning-bg)', border: 'var(--warning-border)', color: 'var(--warning-text)' },
  Low: { bg: 'var(--success-bg)', border: 'var(--success-border)', color: 'var(--success-text)' },
};

// Optimization-card badge palette (badge has no border in the DC).
export const OPTIMIZATION_BADGE_STYLES: Record<OptimizationType, { bg: string; color: string }> = {
  High: { bg: 'var(--infrared-coral-10)', color: 'var(--infrared-coral)' },
  Strategic: { bg: 'var(--signal-blue-10)', color: 'var(--signal-blue)' },
  Medium: { bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
};

// ── Model dot colors by normalized provider key (spec §4.5) ──────────────────
export const MODEL_DOT_COLORS: Record<string, string> = {
  openai: 'var(--signal-green)',     // ChatGPT + ChatGPT (Web)
  claude: 'var(--signal-blue)',
  gemini: 'var(--spectral-violet)',
  perplexity: 'var(--infrared-coral)',
};
export const MODEL_DOT_FALLBACK = 'var(--border)';

// ── Table cell encodings (spec §7.4) ─────────────────────────────────────────
// Status tokens in model-cell values.
export const TOKEN_COLORS: Record<string, string> = {
  Stated: 'var(--success-text)',
  Primary: 'var(--success-text)',
  Generic: 'var(--warning-text)',
  Secondary: 'var(--signal-blue)',
  Absent: 'var(--muted-foreground)',
  'Not used': 'var(--muted-foreground)',
};

// Consensus pill bg/text.
export const CONSENSUS_COLORS: Record<string, string> = {
  Strong: 'var(--success-text)',
  Aligned: 'var(--success-text)',
  Divergent: 'var(--infrared-coral)',
  Weak: 'var(--warning-text)',
  Partial: 'var(--signal-blue)',
};
export const CONSENSUS_BG: Record<string, string> = {
  Strong: 'var(--success-bg)',
  Aligned: 'var(--success-bg)',
  Divergent: 'var(--infrared-coral-10)',
  Weak: 'var(--warning-bg)',
  Partial: 'var(--signal-blue-10)',
};

// ── Gap severity pill styles (spec §8) ───────────────────────────────────────
export const GAP_SEVERITY_STYLES: Record<string, { bg: string; color: string }> = {
  High: { bg: 'var(--infrared-coral-10)', color: 'var(--infrared-coral)' },
  Moderate: { bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
  Low: { bg: 'var(--muted)', color: 'var(--muted-foreground)' },
};

// The canonical Signal-Analyst orb gradient (identical at every size, app-wide).
export const ORB_GRADIENT =
  'linear-gradient(135deg,var(--spectral-violet) 0%,var(--spectral-violet) 62%,var(--signal-blue) 155%)';
